import { useEffect, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { FormikProvider, useFormik } from "formik"
import * as Yup from "yup"
import { makeStyles } from "tss-react/mui"
import { Box, Dialog, Divider } from "@mui/material"

import { USER_REPORTS_TAKEAWAY_SUCCESS_ROUTE } from "../../../../../../constants/routes"
import {
    MAX_EMAIL_LENGTH,
    MAX_NAME_LENGTH,
    MAX_ORDER_COMMENT_LENGTH,
} from "../../../../../../constants/inputValidationDefaults"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { getIncludedVAT, toDineroOrUndefined, toMonetaryAmount } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { isPaymentGatewayDetails } from "../../../../../../modules/payments/paymentTypes"
import {
    isOrderResponse,
    isOrdersResponse,
    isPaymentPollingResponse,
} from "../../../../../../modules/payments/transactionTypes"
import { OrganizerType } from "../../../../../types/OrganizerTypes"
import { BuyerParty } from "../../../../../../modules/payments/orderTypes"
import { DeliveryFulfillmentType } from "../../form/formTypes"
import { StandardLoadingButton } from "../../../../buttons/LoadingButton"
import { SafeArea } from "../../../../skeleton/SafeArea"

import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { selectEffectiveUserId } from "../../../../../../modules/context/contextSelectors"

import type {
    TakeawayCartItem,
    TakeawayDeliveryLocation,
    TakeawayKitchen,
    TakeawayOrderDelivery,
} from "../takeawayTypes"
import { TakeawayDeliveryLocationType } from "../takeawayTypes"
import {
    createTakeawayOrder,
    fetchTakeawayDeliveryLocations,
    fetchTakeawayPaymentDetails,
    TakeawayQuery,
} from "../takeawayApi"

import { calcTotal, calcWalletTotal, CheckoutFormValues, isValidPaymentMethod } from "./takeawayCheckoutLib"
import { TakeawayCheckoutHeader } from "./TakeawayCheckoutHeader"
import { TakeawayCheckoutPaymentSection } from "./TakeawayCheckoutPaymentSection"
import { TakeawayCheckoutGuestSection } from "./TakeawayCheckoutGuestSection"
import { TakeawayCheckoutPersonalInfoSection, useHasTakeawayPersonalInfo } from "./TakeawayCheckoutPersonalInfoSection"
import { TakeawayDeliverySection } from "./TakeawayDeliverySection"
import { TakeawayCheckoutOrderSummary } from "./TakeawayCheckoutOrderSummary"
import { TakeawayCheckoutTermsSection } from "./TakeawayCheckoutTermsSection"

const useStyles = makeStyles()((theme) => ({
    layout: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
        background: theme.palette.common.white,
    },
    body: {
        flex: 1,
        display: "flex",
        alignItems: "flex-start",
        maxWidth: "1440px",
        width: "100%",
        margin: "auto",
    },
    leftColumn: {
        width: "50%",
        flexShrink: 0,
        padding: "2rem 5rem",
        display: "flex",
        flexDirection: "column",
    },
    leftInner: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
    },
    rightColumn: {
        position: "sticky",
        top: "5.625rem",
        alignSelf: "flex-start",
        width: "50%",
        flexShrink: 0,
        padding: "2rem 6.4375rem",
    },
    confirmButton: {
        width: "100%",
        "&.Mui-disabled": {
            backgroundColor: theme.palette.grey[300],
        },
    },
}))

const initialValues: CheckoutFormValues = {
    selectedMethodValue: "",
    orderComment: "",
    accountingValues: {},
    guestName: "",
    guestEmail: "",
    guestPhone: "",
    termsAccepted: false,
    deliveryLocationId: null,
}

type TakeawayCheckoutDialogProps = Readonly<{
    cartByDate: Record<string, TakeawayCartItem[]>
    kitchen: TakeawayKitchen
    onClose: () => void
    onSuccess: () => void
    onCorrectOrder: () => void
}>

export function TakeawayCheckoutDialog({
    cartByDate,
    kitchen,
    onClose,
    onSuccess,
    onCorrectOrder,
}: TakeawayCheckoutDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()
    const navigate = useNavigate()
    const userId = useAppSelector(selectEffectiveUserId)
    const hasPersonalInfo = useHasTakeawayPersonalInfo()

    const sortedDates = useMemo(() => Object.keys(cartByDate).sort(), [cartByDate])
    const allItems = useMemo(() => Object.values(cartByDate).flat(), [cartByDate])
    const totalPrice = useMemo(() => calcTotal(allItems), [allItems])
    const vatAmount = useMemo(() => getIncludedVAT(totalPrice), [totalPrice])
    const walletTotal = useMemo(() => calcWalletTotal(allItems), [allItems])

    const { data: deliveryLocationsResponse } = useApiQuery(fetchTakeawayDeliveryLocations, {
        queryName: TakeawayQuery.TAKEAWAY_DELIVERY_LOCATIONS,
        dependencies: { userId: userId ?? 0, shopId: kitchen.id },
        enabled: Boolean(userId),
        select: (data) => data.deliveryLocations,
    })

    const locations: TakeawayDeliveryLocation[] = deliveryLocationsResponse ?? []

    const baseDeliveries: TakeawayOrderDelivery[] = useMemo(
        () =>
            sortedDates.map((dateKey) => ({
                deliveryType: DeliveryFulfillmentType.PICK_UP,
                deliveryLocation: { name: kitchen.name },
                deliveryTime: dateTimeFactory.fromISO(dateKey).toISO() ?? dateKey,
                orderLines: (cartByDate[dateKey] ?? [])
                    .filter((item) => item.quantity > 0)
                    .map((item) => ({
                        productId: item.product.id,
                        items: item.quantity,
                        buyerParty: BuyerParty.PRIVATE,
                    })),
            })),
        [cartByDate, sortedDates],
    )

    const { data: paymentDetails, isInitialLoading: isLoadingPaymentDetails } = useApiQuery(
        fetchTakeawayPaymentDetails,
        {
            queryName: TakeawayQuery.TAKEAWAY_PAYMENT_DETAILS,
            dependencies: { kitchenId: kitchen.id, deliveries: baseDeliveries },
            enabled: allItems.length > 0,
        },
    )

    const walletBalance = useMemo(
        () => toDineroOrUndefined(paymentDetails?.userAccountBalance),
        [paymentDetails?.userAccountBalance],
    )

    const fillGuestDetails = paymentDetails?.deliveryDetails?.fillGuestDetails ?? false
    const salesConditionsUrl = paymentDetails?.salesConditionsUrl

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                selectedMethodValue: Yup.string().required(l10n.getString("takeaway-checkout-payment-method-required")),
                guestName: fillGuestDetails
                    ? Yup.string().required(l10n.getString("required-field")).max(MAX_NAME_LENGTH)
                    : Yup.string(),
                guestEmail: fillGuestDetails
                    ? Yup.string().required(l10n.getString("required-field")).email().max(MAX_EMAIL_LENGTH)
                    : Yup.string(),
                orderComment: Yup.string().max(
                    MAX_ORDER_COMMENT_LENGTH,
                    l10n.getString("takeaway-checkout-comment-max-length", { max: MAX_ORDER_COMMENT_LENGTH }),
                ),
                termsAccepted: salesConditionsUrl
                    ? Yup.boolean().oneOf([true], l10n.getString("takeaway-checkout-terms-not-accepted"))
                    : Yup.boolean(),
                deliveryLocationId:
                    locations.length > 0
                        ? Yup.mixed().nullable().required(l10n.getString("create-order-delivery-location-required"))
                        : Yup.mixed().nullable(),
            }),
        [fillGuestDetails, salesConditionsUrl, locations.length, l10n],
    )

    const selectedLocationRef = useRef<TakeawayDeliveryLocation | null>(null)

    const { mutate: submitOrder, isLoading: isSubmitting } = useApiMutation(createTakeawayOrder, {
        async onSuccess(_, response) {
            // Payment gateway redirect takes priority -> CREDIT_CARD/MobilePay responses include
            // both `orders` and `paymentDetails`, but we must redirect to the payment window first.
            if (isPaymentPollingResponse(response)) {
                if (isPaymentGatewayDetails(response.paymentDetails)) {
                    window.location.href = response.paymentDetails.paymentWindowUrl
                    return
                }
                return
            }
            if (isOrdersResponse(response)) {
                navigate(USER_REPORTS_TAKEAWAY_SUCCESS_ROUTE, {
                    state: {
                        orders: response.orders,
                        kitchen,
                        totalPrice: toMonetaryAmount(totalPrice),
                        selectedLocation: selectedLocationRef.current,
                    },
                })
                onSuccess()
                return
            }
            if (isOrderResponse(response)) {
                navigate(USER_REPORTS_TAKEAWAY_SUCCESS_ROUTE, {
                    state: {
                        orders: [response.order],
                        kitchen,
                        totalPrice: toMonetaryAmount(totalPrice),
                        selectedLocation: selectedLocationRef.current,
                    },
                })
                onSuccess()
                return
            }
            // Fallback: navigate without order data
            navigate(USER_REPORTS_TAKEAWAY_SUCCESS_ROUTE)
            onSuccess()
        },
    })

    const formik = useFormik<CheckoutFormValues>({
        initialValues,
        validationSchema,
        onSubmit: (values) => {
            const {
                selectedMethodValue,
                deliveryLocationId,
                accountingValues,
                guestName,
                guestEmail,
                guestPhone,
                orderComment,
            } = values

            if (!selectedMethodValue) return
            if (locations.length > 0 && deliveryLocationId === null) return

            const selectedMethod = paymentDetails?.paymentMethods.find((m) => m.value === selectedMethodValue) ?? null

            const dimensions = selectedMethod?.accounting?.dimensions
            const accountingDimensions = dimensions
                ? dimensions
                      .map((dim) => ({ id: dim.id, value: accountingValues[dim.inputField.name] ?? "" }))
                      .filter((dim) => dim.value !== undefined && dim.value !== null && dim.value !== "")
                : undefined

            const organizers = fillGuestDetails
                ? [
                      {
                          name: guestName,
                          email: guestEmail,
                          mobilePhone: guestPhone,
                          organizerType: OrganizerType.CREATOR,
                      },
                  ]
                : undefined

            const submitLocation =
                deliveryLocationId !== null ? (locations.find((l) => l.id === deliveryLocationId) ?? null) : null

            const submitDeliveries: TakeawayOrderDelivery[] = submitLocation
                ? baseDeliveries.map((d) => ({
                      ...d,
                      deliveryLocation: {
                          type: TakeawayDeliveryLocationType.DELIVERY_LOCATION,
                          id: submitLocation.id,
                      },
                  }))
                : baseDeliveries

            submitOrder({
                request: {
                    kitchen: { id: kitchen.id },
                    webshop: { uid: kitchen.webshop?.uid ?? "" },
                    payment: {
                        method: selectedMethodValue,
                        ...(selectedMethod?.card ? { card: { id: selectedMethod.card.id } } : {}),
                        ...(accountingDimensions && accountingDimensions.length > 0
                            ? { accounting: { dimensions: accountingDimensions } }
                            : {}),
                    },
                    orderNote: orderComment,
                    deliveries: submitDeliveries,
                    ...(organizers ? { organizers } : {}),
                },
            })
        },
    })

    const selectedLocation = useMemo(
        () => locations.find((l) => l.id === formik.values.deliveryLocationId) ?? null,
        [locations, formik.values.deliveryLocationId],
    )
    selectedLocationRef.current = selectedLocation

    const selectedMethod = useMemo(
        () => paymentDetails?.paymentMethods.find((m) => m.value === formik.values.selectedMethodValue) ?? null,
        [paymentDetails?.paymentMethods, formik.values.selectedMethodValue],
    )

    const showSalesConditions = Boolean(salesConditionsUrl)

    function handleSubmit() {
        formik.setTouched({
            selectedMethodValue: true,
            guestName: true,
            guestEmail: true,
            guestPhone: true,
            orderComment: true,
            termsAccepted: true,
            deliveryLocationId: true,
        })
        formik.submitForm()
    }

    useEffect(() => {
        if (locations.length === 1 && formik.values.deliveryLocationId === null) {
            formik.setFieldValue("deliveryLocationId", locations[0].id, false)
        }
    }, [locations.length])

    useEffect(() => {
        const methods = paymentDetails?.paymentMethods ?? []
        if (!methods.length || formik.values.selectedMethodValue) return
        if (methods.length === 1 && isValidPaymentMethod(methods[0], walletTotal, walletBalance)) {
            formik.setFieldValue("selectedMethodValue", methods[0].value, false)
        }
    }, [paymentDetails])

    useEffect(() => {
        if (selectedMethod && !isValidPaymentMethod(selectedMethod, walletTotal, walletBalance)) {
            formik.setFieldValue("selectedMethodValue", "", false)
        }
    }, [selectedMethod, walletTotal, walletBalance])

    return (
        <FormikProvider value={formik}>
            <Dialog open onClose={onClose} fullWidth fullScreen scroll="paper">
                <SafeArea>
                    <Box className={classes.layout}>
                        <TakeawayCheckoutHeader onGoBack={onCorrectOrder} onClose={onClose} />

                        <Box className={classes.body}>
                            <Box className={classes.leftColumn}>
                                <Box className={classes.leftInner}>
                                    {fillGuestDetails ? (
                                        <>
                                            <TakeawayCheckoutGuestSection />
                                            <Divider />
                                        </>
                                    ) : hasPersonalInfo ? (
                                        <>
                                            <TakeawayCheckoutPersonalInfoSection />
                                            <Divider />
                                        </>
                                    ) : null}

                                    <TakeawayDeliverySection
                                        locations={locations}
                                        selectedLocationId={formik.values.deliveryLocationId}
                                        onLocationSelect={(id) => formik.setFieldValue("deliveryLocationId", id, true)}
                                    />

                                    <Divider />

                                    <TakeawayCheckoutPaymentSection
                                        isLoading={isLoadingPaymentDetails}
                                        methods={paymentDetails?.paymentMethods ?? []}
                                        selectedMethodValue={formik.values.selectedMethodValue}
                                        walletTotal={walletTotal}
                                        walletBalance={walletBalance}
                                        moneyFactory={moneyFactory}
                                        accountingValues={formik.values.accountingValues}
                                        notice={selectedMethod?.notice}
                                        accountingDimensions={selectedMethod?.accounting}
                                        onMethodSelect={(value) => {
                                            formik.setValues({
                                                ...formik.values,
                                                selectedMethodValue: value,
                                                accountingValues: {},
                                            })
                                        }}
                                        onAccountingChange={(name, value) =>
                                            formik.setFieldValue("accountingValues", {
                                                ...formik.values.accountingValues,
                                                [name]: value,
                                            })
                                        }
                                    />

                                    {showSalesConditions && salesConditionsUrl && (
                                        <TakeawayCheckoutTermsSection salesConditionsUrl={salesConditionsUrl} />
                                    )}

                                    <StandardLoadingButton
                                        loading={isSubmitting}
                                        disabled={formik.submitCount > 0 && !formik.isValid}
                                        onClick={handleSubmit}
                                        color="black"
                                        size="large"
                                        className={classes.confirmButton}
                                    >
                                        <LocalizedStrict
                                            id="takeaway-checkout-confirm-button-with-price"
                                            vars={{ price: moneyFactory.format(totalPrice) }}
                                        >
                                            <span>Complete payment: {moneyFactory.format(totalPrice)}</span>
                                        </LocalizedStrict>
                                    </StandardLoadingButton>
                                </Box>
                            </Box>

                            <Box className={classes.rightColumn}>
                                <TakeawayCheckoutOrderSummary
                                    cartByDate={cartByDate}
                                    sortedDates={sortedDates}
                                    kitchen={kitchen}
                                    moneyFactory={moneyFactory}
                                    totalPrice={totalPrice}
                                    vatAmount={vatAmount}
                                    onCorrectOrder={onCorrectOrder}
                                />
                            </Box>
                        </Box>
                    </Box>
                </SafeArea>
            </Dialog>
        </FormikProvider>
    )
}
